// pages/Admin/SpeakersAdmin.jsx
// Route: /speakers-admin  (add to your react-router routes)
// Manages: SpeakersOverview text+image | Speaker cards | FAQ section

import React, { useState, useEffect, useRef } from "react";
import axios from "../../api/axiosInstance";
import App from "../../Utils/LayoutMobile";

/* ─── Tokens ─────────────────────────────────────────────── */
const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg,${C_PINK},${C_PURPLE})`;

/* ─── Toast ──────────────────────────────────────────────── */
let _tid = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = ++_tid;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  };
  return { toasts, success: (m) => add(m,"success"), error: (m) => add(m,"error"), warning: (m) => add(m,"warning") };
}

/* ─── UploadField ─────────────────────────────────────────── */
function UploadField({ label, fileList, onChange, hint, aspectRatio = "wide" }) {
  const ref  = useRef();
  const file = fileList?.[0];

  const handle = (files) => {
    if (!files?.length) return;
    const f = files[0];
    onChange([{ uid: Date.now(), name: f.name, originFileObj: f, url: URL.createObjectURL(f) }]);
  };

  return (
    <div className="sa-field">
      {label && <label className="sa-label">{label}</label>}
      <div
        className={`sa-upload ${file ? "sa-upload--has" : ""} sa-upload--${aspectRatio}`}
        onClick={() => ref.current?.click()}
        onDragOver={(e)=>{ e.preventDefault(); e.currentTarget.classList.add("sa-upload--drag"); }}
        onDragLeave={(e)=>e.currentTarget.classList.remove("sa-upload--drag")}
        onDrop={(e)=>{ e.preventDefault(); e.currentTarget.classList.remove("sa-upload--drag"); handle(e.dataTransfer.files); }}
      >
        <input ref={ref} type="file" accept="image/*" style={{display:"none"}}
          onChange={(e)=>handle(e.target.files)}/>
        {file ? (
          <div className="sa-upload-prev">
            <img src={file.url} alt="preview" className="sa-upload-thumb"/>
            <div style={{flex:1,minWidth:0}}>
              <span className="sa-upload-name">{file.name}</span>
              <span className="sa-upload-change">Click to change</span>
            </div>
            <button className="sa-icon-btn sa-icon-btn--sm"
              onClick={(e)=>{ e.stopPropagation(); onChange([]); }}>
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        ) : (
          <div className="sa-upload-empty">
            <svg width="20" height="20" fill="none" stroke={C_PINK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
            </svg>
            <span>Drop or <strong>browse</strong></span>
            {hint && <span className="sa-upload-hint">{hint}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SectionCard ─────────────────────────────────────────── */
function SectionCard({ title, subtitle, icon, iconColor, iconBg, delay=0, children }) {
  return (
    <div className="sa-card" style={{animationDelay:`${delay}s`}}>
      <div className="sa-card-hdr">
        <span className="sa-card-ico" style={{color:iconColor,background:iconBg}}>{icon}</span>
        <div>
          <h2 className="sa-card-title">{title}</h2>
          {subtitle && <p className="sa-card-sub">{subtitle}</p>}
        </div>
      </div>
      <div className="sa-card-body">{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
export default function SpeakersAdmin() {
  const toast = useToast();
  const [tab,     setTab]     = useState("overview");
  const [loading, setLoading] = useState(true);

  /* ══ OVERVIEW state ══ */
  const [ov, setOv] = useState({
    eyebrowText:"Skinola International Aesthetics", titleMain:"Learn from the",
    titleHighlight:"Best Team!", leadText:"", bodyText1:"", bodyText2:"",
    imageAlt:"Speakers at Skinola", badgeValue:"60+", badgeLabel:"Global Speakers",
  });
  const [ovImage,   setOvImage]   = useState([]);
  const [ovSaving,  setOvSaving]  = useState(false);

  /* ══ SPEAKERS state ══ */
  const [speakers,  setSpeakers]  = useState([]);
  const [spModal,   setSpModal]   = useState(null); // null | "create" | speakerId
  const [spForm,    setSpForm]    = useState({ name:"", designation:"", specialty:"", institution:"", country:"", image:[] });
  const [spSaving,  setSpSaving]  = useState(false);
  const [spDel,     setSpDel]     = useState(null);

  /* ══ FAQ state ══ */
  const [faqSection, setFaqSection] = useState({
    eyebrowText:"Questions for speakers", titleMain:"Speakers",
    titleHighlight:"FAQs",
    subtitle:"Everything you need to know about the Skinola International Aesthetics workshop.",
    faqs:[{ q:"", a:"" }],
  });
  const [faqSaving, setFaqSaving] = useState(false);

  /* ── Fetch all ── */
  useEffect(() => {
    axios.get("/api/v1/speakers-page")
      .then((res) => {
        const d = res.data?.data || {};
        if (d.overview) {
          const o = d.overview;
          setOv({
            eyebrowText:    o.eyebrowText    || "Skinola International Aesthetics",
            titleMain:      o.titleMain      || "Learn from the",
            titleHighlight: o.titleHighlight || "Best Team!",
            leadText:       o.leadText       || "",
            bodyText1:      o.bodyText1      || "",
            bodyText2:      o.bodyText2      || "",
            imageAlt:       o.imageAlt       || "",
            badgeValue:     o.badgeValue     || "60+",
            badgeLabel:     o.badgeLabel     || "Global Speakers",
          });
          if (o.imageSrc) setOvImage([{ uid:"-ov", name:"overview.jpg", url:o.imageSrc }]);
        }
        if (d.speakers?.length) setSpeakers(d.speakers);
        if (d.faqSection) {
          const f = d.faqSection;
          setFaqSection({
            eyebrowText:    f.eyebrowText    || "Questions for speakers",
            titleMain:      f.titleMain      || "Speakers",
            titleHighlight: f.titleHighlight || "FAQs",
            subtitle:       f.subtitle       || "",
            faqs:           f.faqs?.length   ? f.faqs : [{ q:"", a:"" }],
          });
        }
      })
      .catch(() => toast.error("Failed to load speakers page data"))
      .finally(() => setLoading(false));
  }, []);

  /* ══ SAVE OVERVIEW ══ */
  const saveOverview = async () => {
    setOvSaving(true);
    try {
      const fd = new FormData();
      Object.entries(ov).forEach(([k,v]) => fd.append(k, v));
      const img = ovImage?.[0];
      if (img?.originFileObj) fd.append("overviewImage", img.originFileObj);
      else if (img?.url)      fd.append("imageSrcUrl", img.url);

      await axios.post("/api/v1/speakers-page/overview", fd, {
        headers:{ "Content-Type":"multipart/form-data" }
      });
      toast.success("Overview saved!");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally { setOvSaving(false); }
  };

  /* ══ SPEAKER MODAL ══ */
  const openCreate = () => {
    setSpForm({ name:"", designation:"", specialty:"", institution:"", country:"", image:[] });
    setSpModal("create");
  };
  const openEdit = (sp) => {
    setSpForm({
      name:        sp.name        || "",
      designation: sp.designation || "",
      specialty:   sp.specialty   || "",
      institution: sp.institution || "",
      country:     sp.country     || "",
      image: sp.imageUrl ? [{ uid:"-sp", name:"speaker.jpg", url:sp.imageUrl }] : [],
    });
    setSpModal(sp._id);
  };

  const saveSpeaker = async () => {
    if (!spForm.name.trim()) { toast.error("Name is required"); return; }
    setSpSaving(true);
    try {
      const fd = new FormData();
      fd.append("name",        spForm.name);
      fd.append("designation", spForm.designation);
      fd.append("specialty",   spForm.specialty);
      fd.append("institution", spForm.institution);
      fd.append("country",     spForm.country);
      const img = spForm.image?.[0];
      if (img?.originFileObj) fd.append("speakerImage", img.originFileObj);
      else if (img?.url)      fd.append("imageUrl", img.url);

      const headers = { "Content-Type":"multipart/form-data" };
      if (spModal === "create") {
        const r = await axios.post("/api/v1/speakers-page/speaker", fd, { headers });
        setSpeakers(r.data.data);
        toast.success("Speaker created!");
      } else {
        const r = await axios.put(`/api/v1/speakers-page/speaker/${spModal}`, fd, { headers });
        setSpeakers(r.data.data);
        toast.success("Speaker updated!");
      }
      setSpModal(null);
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally { setSpSaving(false); }
  };

  const deleteSpeaker = async (id) => {
    if (!window.confirm("Delete this speaker?")) return;
    setSpDel(id);
    try {
      const r = await axios.delete(`/api/v1/speakers-page/speaker/${id}`);
      setSpeakers(r.data.data);
      toast.success("Speaker deleted");
    } catch { toast.error("Delete failed"); }
    finally   { setSpDel(null); }
  };

  /* ══ SAVE FAQs ══ */
  const saveFaqs = async () => {
    const invalid = faqSection.faqs.some((f) => !f.q.trim() || !f.a.trim());
    if (invalid) { toast.error("All FAQ questions and answers must be filled"); return; }
    setFaqSaving(true);
    try {
      await axios.post("/api/v1/speakers-page/faqs", faqSection);
      toast.success("FAQs saved!");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally { setFaqSaving(false); }
  };

  const addFaq    = ()      => setFaqSection((p)=>({...p,faqs:[...p.faqs,{q:"",a:""}]}));
  const removeFaq = (i)     => setFaqSection((p)=>({...p,faqs:p.faqs.filter((_,idx)=>idx!==i)}));
  const updFaq    = (i,k,v) => setFaqSection((p)=>{const f=[...p.faqs];f[i]={...f[i],[k]:v};return{...p,faqs:f};});

  /* ── Icons ── */
  const OvIcon  = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>;
  const SpIcon  = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
  const FaqIcon = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  const PlusIcon= <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
  const TrashIcon=<svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>;
  const EditIcon= <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
  const SaveIcon= <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
  const CloseIcon=<svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>;

  return (
    <App>
      <div className="sa-page">

        {/* Toasts */}
        <div className="sa-toast-wrap">
          {toast.toasts.map((t)=>(
            <div key={t.id} className={`sa-toast sa-toast--${t.type}`}>
              {t.type==="success"?<svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>:<svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
              <span>{t.msg}</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="sa-header">
          <div className="sa-eyebrow"><span className="sa-eyebrow-dot"/>Speakers Page</div>
          <h1 className="sa-page-title">Speakers <span className="sa-grad">Management</span></h1>
          <p className="sa-page-sub">Manage the Speakers overview section, individual speaker cards, and FAQs.</p>
        </div>

        {loading ? (
          <div className="sa-loading"><div className="sa-spinner"/><span>Loading…</span></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="sa-tabs">
              {[
                { id:"overview",  label:"Overview",  icon:OvIcon  },
                { id:"faqs",      label:"FAQs",       icon:FaqIcon },
              ].map((t)=>(
                <button key={t.id} className={`sa-tab ${tab===t.id?"sa-tab--active":""}`}
                  onClick={()=>setTab(t.id)}>
                  {t.icon} {t.label}
                  {t.id==="speakers" && <span className="sa-tab-badge">{speakers.length}</span>}
                  {t.id==="faqs"     && <span className="sa-tab-badge">{faqSection.faqs.length}</span>}
                </button>
              ))}
            </div>

            {/* ══════════ OVERVIEW TAB ══════════ */}
            {tab==="overview" && (
              <div className="sa-panel">
                <SectionCard title="Overview Text — Left Side"
                  subtitle="Eyebrow, title, lead paragraph and body paragraphs"
                  icon={OvIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}>

                  <div className="sa-grid-3">
                    <div className="sa-field">
                      <label className="sa-label">Eyebrow Badge</label>
                      <input className="sa-input" value={ov.eyebrowText}
                        onChange={(e)=>setOv(p=>({...p,eyebrowText:e.target.value}))}
                        placeholder="Skinola International Aesthetics"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Title — Main</label>
                      <input className="sa-input" value={ov.titleMain}
                        onChange={(e)=>setOv(p=>({...p,titleMain:e.target.value}))}
                        placeholder="Learn from the"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Title — Pink Highlight</label>
                      <input className="sa-input" value={ov.titleHighlight}
                        onChange={(e)=>setOv(p=>({...p,titleHighlight:e.target.value}))}
                        placeholder="Best Team!"/>
                    </div>
                  </div>

                  <div className="sa-field">
                    <label className="sa-label">Lead Paragraph (first — with bold text)</label>
                    <textarea className="sa-textarea" rows={3} value={ov.leadText}
                      onChange={(e)=>setOv(p=>({...p,leadText:e.target.value}))}
                      placeholder="Skinola International Aesthetics brings together a hand-picked faculty of over 60 world-renowned specialists…"/>
                    <span className="sa-hint">💡 Use **text** to mark bold parts → e.g. <code>**60 world-renowned specialists**</code></span>
                  </div>

                  <div className="sa-field">
                    <label className="sa-label">Body Paragraph 2</label>
                    <textarea className="sa-textarea" rows={3} value={ov.bodyText1}
                      onChange={(e)=>setOv(p=>({...p,bodyText1:e.target.value}))}
                      placeholder="From pioneering regenerative techniques…"/>
                  </div>

                  <div className="sa-field">
                    <label className="sa-label">Body Paragraph 3</label>
                    <textarea className="sa-textarea" rows={3} value={ov.bodyText2}
                      onChange={(e)=>setOv(p=>({...p,bodyText2:e.target.value}))}
                      placeholder="Beyond lectures and workshops, our faculty engage personally…"/>
                  </div>
                </SectionCard>

                <SectionCard title="Right Side — Image & Badge"
                  subtitle="Conference/speaker image and floating badge (e.g. 60+ Global Speakers)"
                  icon={<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                  iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.1}>

                  <UploadField
                    label="Overview Section Image (right side)"
                    hint="Recommended: 4:3 ratio, min 800×600px"
                    fileList={ovImage}
                    onChange={setOvImage}
                    aspectRatio="wide"
                  />

                  {ovImage[0] && (
                    <div className="sa-img-prev-wrap">
                      <img src={ovImage[0].url} alt="preview" className="sa-img-prev"/>
                    </div>
                  )}

                  <div className="sa-grid-3">
                    <div className="sa-field">
                      <label className="sa-label">Image Alt Text</label>
                      <input className="sa-input" value={ov.imageAlt}
                        onChange={(e)=>setOv(p=>({...p,imageAlt:e.target.value}))}
                        placeholder="Speakers at Skinola"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Badge Value</label>
                      <input className="sa-input" value={ov.badgeValue}
                        onChange={(e)=>setOv(p=>({...p,badgeValue:e.target.value}))}
                        placeholder="60+"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Badge Label</label>
                      <input className="sa-input" value={ov.badgeLabel}
                        onChange={(e)=>setOv(p=>({...p,badgeLabel:e.target.value}))}
                        placeholder="Global Speakers"/>
                    </div>
                  </div>

                  {/* Live badge preview */}
                  <div className="sa-badge-preview">
                    <p className="sa-preview-lbl">Badge Preview</p>
                    <div className="sa-badge-demo">
                      <span className="sa-badge-demo-val">{ov.badgeValue || "60+"}</span>
                      <span className="sa-badge-demo-lbl">{ov.badgeLabel || "Global Speakers"}</span>
                    </div>
                  </div>
                </SectionCard>

                <div className="sa-save-bar">
                  <div className="sa-save-info">
                    <svg width="13" height="13" fill="none" stroke="#9a86b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Saves overview text and right-side image.
                  </div>
                  <button className="sa-save-btn" onClick={saveOverview} disabled={ovSaving}>
                    {ovSaving?<><span className="sa-spin"/><span>Saving…</span></>:<>{SaveIcon}<span>Save Overview</span></>}
                  </button>
                </div>
              </div>
            )}

            {/* ══════════ SPEAKERS TAB ══════════ */}
            {tab==="speakers" && (
              <div className="sa-panel">
                <SectionCard title="Speaker Cards"
                  subtitle="Each card shows on the Speakers grid — name, designation, specialty and photo"
                  icon={SpIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}>

                  {/* Stats */}
                  <div className="sa-sp-stats">
                    <div className="sa-sp-stat"><span className="sa-sp-stat-num">{speakers.length}</span><span className="sa-sp-stat-lbl">Total Speakers</span></div>
                    <div className="sa-sp-stat"><span className="sa-sp-stat-num">{speakers.filter(s=>s.imageUrl).length}</span><span className="sa-sp-stat-lbl">With Photos</span></div>
                    <div className="sa-sp-stat"><span className="sa-sp-stat-num">{[...new Set(speakers.map(s=>s.country).filter(Boolean))].length}</span><span className="sa-sp-stat-lbl">Countries</span></div>
                  </div>

                  {speakers.length===0 ? (
                    <div className="sa-empty">
                      <div className="sa-empty-ico">{SpIcon}</div>
                      <p className="sa-empty-title">No speakers yet</p>
                      <p className="sa-empty-sub">Add your first speaker card below.</p>
                    </div>
                  ) : (
                    <div className="sa-sp-grid">
                      {[...speakers].sort((a,b)=>(a.order||0)-(b.order||0)).map((sp)=>(
                        <div key={sp._id} className="sa-sp-card">
                          <div className="sa-sp-photo">
                            {sp.imageUrl
                              ? <img src={sp.imageUrl} alt={sp.name} className="sa-sp-img"/>
                              : <div className="sa-sp-img-placeholder">
                                  <svg width="24" height="24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                </div>
                            }
                            {sp.country && <span className="sa-sp-country">{sp.country}</span>}
                          </div>
                          <div className="sa-sp-info">
                            <div className="sa-sp-name">{sp.name}</div>
                            {sp.designation && <div className="sa-sp-desig">{sp.designation}</div>}
                            {sp.specialty && (
                              <div className="sa-sp-tag">{sp.specialty}</div>
                            )}
                            {sp.institution && <div className="sa-sp-inst">{sp.institution}</div>}
                          </div>
                          <div className="sa-sp-actions">
                            <button className="sa-icon-btn sa-icon-btn--edit" onClick={()=>openEdit(sp)} title="Edit">{EditIcon}</button>
                            <button className="sa-icon-btn sa-icon-btn--del" onClick={()=>deleteSpeaker(sp._id)}
                              disabled={spDel===sp._id} title="Delete">
                              {spDel===sp._id?<span className="sa-spin sa-spin--sm"/>:TrashIcon}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button className="sa-add-btn sa-add-btn--full" onClick={openCreate}>
                    {PlusIcon} Add New Speaker
                  </button>
                </SectionCard>
              </div>
            )}

            {/* ══════════ FAQs TAB ══════════ */}
            {tab==="faqs" && (
              <div className="sa-panel">
                <SectionCard title="FAQ Section Header"
                  subtitle="Section eyebrow, title, and subtitle shown above the FAQ accordion"
                  icon={FaqIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}>

                  <div className="sa-grid-3">
                    <div className="sa-field">
                      <label className="sa-label">Eyebrow</label>
                      <input className="sa-input" value={faqSection.eyebrowText}
                        onChange={(e)=>setFaqSection(p=>({...p,eyebrowText:e.target.value}))}
                        placeholder="Questions for speakers"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Title — Main</label>
                      <input className="sa-input" value={faqSection.titleMain}
                        onChange={(e)=>setFaqSection(p=>({...p,titleMain:e.target.value}))}
                        placeholder="Speakers"/>
                    </div>
                    <div className="sa-field">
                      <label className="sa-label">Title — Highlight (pink)</label>
                      <input className="sa-input" value={faqSection.titleHighlight}
                        onChange={(e)=>setFaqSection(p=>({...p,titleHighlight:e.target.value}))}
                        placeholder="FAQs"/>
                    </div>
                  </div>

                  <div className="sa-field">
                    <label className="sa-label">Subtitle</label>
                    <textarea className="sa-textarea" rows={2} value={faqSection.subtitle}
                      onChange={(e)=>setFaqSection(p=>({...p,subtitle:e.target.value}))}
                      placeholder="Everything you need to know…"/>
                  </div>
                </SectionCard>

                <SectionCard title={`FAQ Questions & Answers (${faqSection.faqs.length})`}
                  subtitle="Each Q&A appears as an accordion item on the Speakers page"
                  icon={FaqIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.1}>

                  <div className="sa-faq-list">
                    {faqSection.faqs.map((faq, i) => (
                      <div key={i} className="sa-faq-row">
                        <div className="sa-faq-num">{i+1}</div>
                        <div className="sa-faq-inputs">
                          <div className="sa-field">
                            <label className="sa-label">Question</label>
                            <input className="sa-input" value={faq.q}
                              onChange={(e)=>updFaq(i,"q",e.target.value)}
                              placeholder="Who are the speakers at Skinola International Aesthetics?"
                              style={{fontWeight:600}}/>
                          </div>
                          <div className="sa-field">
                            <label className="sa-label">Answer</label>
                            <textarea className="sa-textarea" rows={3} value={faq.a}
                              onChange={(e)=>updFaq(i,"a",e.target.value)}
                              placeholder="Our speakers are a hand-picked faculty of over 60 globally recognised…"/>
                          </div>
                        </div>
                        {faqSection.faqs.length > 1 && (
                          <button className="sa-icon-btn sa-icon-btn--del" style={{marginTop:28}}
                            onClick={()=>removeFaq(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="sa-add-btn sa-add-btn--full" onClick={addFaq}>
                    {PlusIcon} Add FAQ
                  </button>
                </SectionCard>

                <div className="sa-save-bar">
                  <div className="sa-save-info">
                    <svg width="13" height="13" fill="none" stroke="#9a86b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Saves FAQ section header and all Q&A items together.
                  </div>
                  <button className="sa-save-btn" onClick={saveFaqs} disabled={faqSaving}>
                    {faqSaving?<><span className="sa-spin"/><span>Saving…</span></>:<>{SaveIcon}<span>Save FAQs</span></>}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ══════════ SPEAKER MODAL ══════════ */}
      {spModal !== null && (
        <div className="sa-overlay"
          onClick={(e)=>e.target.classList.contains("sa-overlay")&&setSpModal(null)}>
          <div className="sa-modal">

            <div className="sa-modal-hdr">
              <div>
                <div className="sa-modal-eyebrow">{spModal==="create"?"New Speaker":"Edit Speaker"}</div>
                <h3 className="sa-modal-title">
                  {spModal==="create"?"Add Speaker Card":spForm.name||"Edit Card"}
                </h3>
              </div>
              <button className="sa-icon-btn" onClick={()=>setSpModal(null)}>{CloseIcon}</button>
            </div>

            <div className="sa-modal-body">

              {/* Photo upload — portrait */}
              <UploadField
                label="Speaker Photo"
                hint="Portrait — min 400×500px, JPG/PNG"
                fileList={spForm.image}
                onChange={(v)=>setSpForm(p=>({...p,image:v}))}
                aspectRatio="portrait"
              />

              {spForm.image[0] && (
                <div style={{display:"flex",justifyContent:"center"}}>
                  <img src={spForm.image[0].url} alt="speaker"
                    style={{width:100,height:120,objectFit:"cover",borderRadius:12,border:"2px solid #ede5f5"}}/>
                </div>
              )}

              <div className="sa-grid-2">
                <div className="sa-field">
                  <label className="sa-label">Full Name <span style={{color:C_PINK}}>*</span></label>
                  <input className="sa-input" value={spForm.name}
                    onChange={(e)=>setSpForm(p=>({...p,name:e.target.value}))}
                    placeholder="Dr. Priya Sharma"/>
                </div>
                <div className="sa-field">
                  <label className="sa-label">Designation</label>
                  <input className="sa-input" value={spForm.designation}
                    onChange={(e)=>setSpForm(p=>({...p,designation:e.target.value}))}
                    placeholder="Senior Dermatologist"/>
                </div>
              </div>

              <div className="sa-grid-2">
                <div className="sa-field">
                  <label className="sa-label">Specialty / Topic</label>
                  <input className="sa-input" value={spForm.specialty}
                    onChange={(e)=>setSpForm(p=>({...p,specialty:e.target.value}))}
                    placeholder="Hair Restoration"/>
                </div>
                <div className="sa-field">
                  <label className="sa-label">Institution / Hospital</label>
                  <input className="sa-input" value={spForm.institution}
                    onChange={(e)=>setSpForm(p=>({...p,institution:e.target.value}))}
                    placeholder="AIIMS, Delhi"/>
                </div>
              </div>

              <div className="sa-field">
                <label className="sa-label">Country</label>
                <input className="sa-input" value={spForm.country}
                  onChange={(e)=>setSpForm(p=>({...p,country:e.target.value}))}
                  placeholder="India"/>
              </div>

            </div>

            <div className="sa-modal-ftr">
              <button className="sa-cancel-btn" onClick={()=>setSpModal(null)}>Cancel</button>
              <button className="sa-save-btn" onClick={saveSpeaker} disabled={spSaving}>
                {spSaving?<><span className="sa-spin"/><span>Saving…</span></>:<>{SaveIcon}<span>{spModal==="create"?"Create Speaker":"Save Changes"}</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes sa-fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes sa-spin-anim{to{transform:rotate(360deg)}}
        @keyframes sa-pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes sa-toast-in{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:translateX(0)}}
        @keyframes sa-modal-in{from{opacity:0;transform:translateY(20px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        *,*::before,*::after{box-sizing:border-box}

        .sa-page{background:#f4eefa;min-height:100vh;padding:28px 32px 80px;display:flex;flex-direction:column;gap:22px;font-family:inherit;position:relative;}

        /* Header */
        .sa-header{animation:sa-fadeUp .4s ease both;}
        .sa-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};background:rgba(240,77,160,0.08);border:1px solid rgba(240,77,160,0.2);padding:5px 14px 5px 10px;border-radius:50px;margin-bottom:12px;}
        .sa-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:${C_PINK};animation:sa-pulse 2s infinite;flex-shrink:0;}
        .sa-page-title{font-size:clamp(22px,2.5vw,30px);font-weight:800;color:${C_DARK};letter-spacing:-.025em;margin:0 0 6px;}
        .sa-grad{background:linear-gradient(90deg,${C_PINK},#c066f0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .sa-page-sub{font-size:13.5px;color:#8a7a9a;margin:0;}

        .sa-loading{display:flex;align-items:center;gap:12px;padding:60px;justify-content:center;color:#8a7a9a;font-size:14px;}
        .sa-spinner{width:28px;height:28px;border-radius:50%;border:3px solid rgba(240,77,160,0.15);border-top-color:${C_PINK};animation:sa-spin-anim .75s linear infinite;}

        /* Tabs */
        .sa-tabs{display:flex;gap:8px;flex-wrap:wrap;}
        .sa-tab{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:12px;border:1.5px solid #e8d8f8;background:#fff;color:#8a7a9a;font-size:13px;font-weight:700;cursor:pointer;transition:all .2s;font-family:inherit;position:relative;}
        .sa-tab--active{background:${C_GRAD};color:#fff;border-color:transparent;box-shadow:0 6px 18px rgba(240,77,160,0.28);}
        .sa-tab:not(.sa-tab--active):hover{border-color:${C_PINK};color:${C_PINK};}
        .sa-tab-badge{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:rgba(255,255,255,0.25);font-size:10px;font-weight:800;color:#fff;}
        .sa-tab:not(.sa-tab--active) .sa-tab-badge{background:rgba(240,77,160,0.12);color:${C_PINK};}

        /* Panel */
        .sa-panel{display:flex;flex-direction:column;gap:20px;}

        /* Section card */
        .sa-card{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);overflow:hidden;animation:sa-fadeUp .4s ease both;}
        .sa-card-hdr{display:flex;align-items:center;gap:14px;padding:20px 26px 18px;border-bottom:1px solid #f3ecfa;background:#faf6fe;}
        .sa-card-ico{width:44px;height:44px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;justify-content:center;}
        .sa-card-title{font-size:16px;font-weight:800;color:${C_DARK};margin:0 0 2px;}
        .sa-card-sub{font-size:12px;color:#9a86b8;margin:0;}
        .sa-card-body{padding:24px 26px 28px;display:flex;flex-direction:column;gap:18px;}

        /* Fields */
        .sa-field{display:flex;flex-direction:column;gap:6px;}
        .sa-label{font-size:12px;font-weight:700;color:#5a5070;letter-spacing:.03em;}
        .sa-hint{font-size:11px;color:#9a86b8;margin-top:3px;}
        .sa-hint code{background:rgba(240,77,160,0.08);padding:1px 5px;border-radius:4px;color:${C_PINK};font-size:11px;}
        .sa-input{height:44px;border-radius:11px;border:1.5px solid #e8d8f8;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;transition:border-color .2s,box-shadow .2s;}
        .sa-input:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .sa-input::placeholder{color:#c4b4d8;}
        .sa-textarea{border-radius:11px;border:1.5px solid #e8d8f8;padding:12px 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;resize:vertical;line-height:1.6;transition:border-color .2s,box-shadow .2s;}
        .sa-textarea:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .sa-textarea::placeholder{color:#c4b4d8;}
        .sa-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .sa-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;}

        /* Upload */
        .sa-upload{border:1.5px dashed #d8c8f0;border-radius:12px;background:#fdfbff;padding:14px;cursor:pointer;display:flex;align-items:center;transition:border-color .2s,background .2s;}
        .sa-upload--wide{min-height:72px;}
        .sa-upload--portrait{min-height:90px;}
        .sa-upload:hover,.sa-upload--drag{border-color:${C_PINK};background:rgba(240,77,160,0.03);}
        .sa-upload--has{border-style:solid;border-color:#e8d8f8;background:#fff;}
        .sa-upload-empty{display:flex;flex-direction:column;align-items:center;gap:6px;width:100%;text-align:center;font-size:12px;color:#8a7a9a;}
        .sa-upload-empty strong{color:${C_PINK};}
        .sa-upload-hint{font-size:11px;color:#b4a4c8;}
        .sa-upload-prev{display:flex;align-items:center;gap:10px;width:100%;}
        .sa-upload-thumb{width:44px;height:44px;object-fit:cover;border-radius:7px;border:1px solid #ede5f5;flex-shrink:0;}
        .sa-upload-name{display:block;font-size:12px;font-weight:700;color:${C_DARK};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .sa-upload-change{font-size:11px;color:#9a86b8;display:block;}

        /* Image preview */
        .sa-img-prev-wrap{border-radius:14px;overflow:hidden;border:1px solid #ede5f5;}
        .sa-img-prev{width:100%;max-height:220px;object-fit:cover;display:block;}

        /* Badge preview */
        .sa-badge-preview{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:14px 18px;}
        .sa-preview-lbl{font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.08em;margin:0 0 10px;}
        .sa-badge-demo{background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.25);border-radius:12px;padding:10px 16px;display:inline-flex;flex-direction:column;gap:3px;background:${C_DARK};}
        .sa-badge-demo-val{font-size:22px;font-weight:800;background:linear-gradient(135deg,${C_PINK},#e090ff);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;}
        .sa-badge-demo-lbl{font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,0.7);}

        /* Speaker stats */
        .sa-sp-stats{display:flex;gap:14px;flex-wrap:wrap;}
        .sa-sp-stat{background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:12px 18px;display:flex;flex-direction:column;gap:3px;min-width:130px;}
        .sa-sp-stat-num{font-size:22px;font-weight:800;background:${C_GRAD};-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;}
        .sa-sp-stat-lbl{font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em;}

        /* Empty */
        .sa-empty{text-align:center;padding:40px;display:flex;flex-direction:column;align-items:center;gap:10px;}
        .sa-empty-ico{width:56px;height:56px;border-radius:14px;background:rgba(240,77,160,0.08);display:flex;align-items:center;justify-content:center;color:${C_PINK};}
        .sa-empty-title{font-size:16px;font-weight:700;color:${C_DARK};margin:0;}
        .sa-empty-sub{font-size:13px;color:#9a86b8;margin:0;}

        /* Speaker cards grid */
        .sa-sp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:14px;}
        .sa-sp-card{background:#faf6fe;border:1px solid #ede5f5;border-radius:14px;overflow:hidden;transition:box-shadow .2s,transform .2s;}
        .sa-sp-card:hover{box-shadow:0 8px 24px rgba(26,0,64,.08);transform:translateY(-2px);}
        .sa-sp-photo{position:relative;height:110px;background:${C_DARK};overflow:hidden;}
        .sa-sp-img{width:100%;height:100%;object-fit:cover;object-position:top;}
        .sa-sp-img-placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;}
        .sa-sp-country{position:absolute;top:8px;right:8px;background:rgba(255,255,255,0.15);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.2);color:#fff;font-size:9px;font-weight:700;padding:3px 7px;border-radius:6px;text-transform:uppercase;letter-spacing:.06em;}
        .sa-sp-info{padding:10px 12px;}
        .sa-sp-name{font-size:13px;font-weight:800;color:${C_DARK};margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .sa-sp-desig{font-size:11.5px;color:#6a5a7a;font-weight:500;margin-bottom:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .sa-sp-tag{display:inline-block;font-size:10px;font-weight:700;padding:2px 8px;border-radius:6px;background:rgba(240,77,160,0.1);color:${C_PINK};margin-bottom:4px;letter-spacing:.04em;}
        .sa-sp-inst{font-size:11px;color:#b4a4c8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .sa-sp-actions{display:flex;gap:6px;padding:8px 12px 10px;border-top:1px solid #ede5f5;}

        /* FAQ list */
        .sa-faq-list{display:flex;flex-direction:column;gap:14px;}
        .sa-faq-row{display:flex;align-items:flex-start;gap:12px;background:#faf6fe;border:1px solid #ede5f5;border-radius:14px;padding:16px;}
        .sa-faq-num{width:28px;height:28px;border-radius:8px;background:${C_GRAD};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;margin-top:2px;}
        .sa-faq-inputs{flex:1;display:flex;flex-direction:column;gap:10px;}

        /* Add btn */
        .sa-add-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:10px;border:1.5px solid rgba(240,77,160,0.3);background:rgba(240,77,160,0.06);color:${C_PINK};font-size:13px;font-weight:700;cursor:pointer;transition:background .2s;font-family:inherit;}
        .sa-add-btn:hover{background:rgba(240,77,160,0.12);}
        .sa-add-btn--full{width:100%;justify-content:center;}

        /* Icon buttons */
        .sa-icon-btn{width:30px;height:30px;border-radius:8px;border:1.5px solid #e8d8f8;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;color:#8a7a9a;flex-shrink:0;}
        .sa-icon-btn--sm{width:26px;height:26px;border-radius:6px;}
        .sa-icon-btn--edit:hover{border-color:${C_PURPLE};color:${C_PURPLE};background:rgba(139,47,201,0.06);}
        .sa-icon-btn--del:hover{border-color:#ef4444;color:#ef4444;background:#fef2f2;}
        .sa-icon-btn--del:disabled{opacity:.4;cursor:not-allowed;}

        /* Save bar */
        .sa-save-bar{display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:16px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);padding:18px 26px;gap:16px;flex-wrap:wrap;}
        .sa-save-info{display:flex;align-items:center;gap:8px;font-size:12px;color:#9a86b8;font-weight:500;}
        .sa-save-btn{display:inline-flex;align-items:center;gap:9px;padding:12px 26px;background:${C_GRAD};color:#fff;font-size:13.5px;font-weight:700;border:none;border-radius:12px;cursor:pointer;box-shadow:0 6px 20px rgba(240,77,160,0.3);transition:transform .2s,box-shadow .2s;font-family:inherit;white-space:nowrap;}
        .sa-save-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 10px 28px rgba(240,77,160,0.42);}
        .sa-save-btn:disabled{opacity:.65;cursor:not-allowed;}
        .sa-cancel-btn{display:inline-flex;align-items:center;padding:12px 22px;border-radius:11px;border:1.5px solid #e8d8f8;background:#fff;color:#5a5070;font-size:13.5px;font-weight:600;cursor:pointer;font-family:inherit;transition:border-color .2s;}
        .sa-cancel-btn:hover{border-color:${C_PURPLE};}
        .sa-spin{width:15px;height:15px;border-radius:50%;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;animation:sa-spin-anim .7s linear infinite;}
        .sa-spin--sm{width:12px;height:12px;}

        /* Modal */
        .sa-overlay{position:fixed;inset:0;background:rgba(26,0,64,.55);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:9998;padding:20px;}
        .sa-modal{background:#fff;border-radius:22px;width:100%;max-width:580px;max-height:90vh;display:flex;flex-direction:column;box-shadow:0 30px 60px rgba(26,0,64,.2);animation:sa-modal-in .35s cubic-bezier(.16,1,.3,1) both;}
        .sa-modal-hdr{display:flex;align-items:flex-start;justify-content:space-between;padding:22px 26px 16px;border-bottom:1px solid #f3ecfa;background:#faf6fe;border-radius:22px 22px 0 0;gap:12px;}
        .sa-modal-eyebrow{font-size:10px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};margin-bottom:4px;}
        .sa-modal-title{font-size:18px;font-weight:800;color:${C_DARK};margin:0;}
        .sa-modal-body{padding:22px 26px;overflow-y:auto;display:flex;flex-direction:column;gap:16px;flex:1;}
        .sa-modal-body::-webkit-scrollbar{width:5px;}
        .sa-modal-body::-webkit-scrollbar-thumb{background:rgba(139,47,201,0.2);border-radius:10px;}
        .sa-modal-ftr{padding:14px 26px 20px;border-top:1px solid #f3ecfa;display:flex;align-items:center;justify-content:flex-end;gap:10px;}

        /* Toasts */
        .sa-toast-wrap{position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .sa-toast{display:inline-flex;align-items:center;gap:9px;padding:11px 16px;border-radius:12px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(26,0,64,.15);animation:sa-toast-in .35s cubic-bezier(.16,1,.3,1) both;max-width:300px;}
        .sa-toast--success{background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;}
        .sa-toast--error{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}

        @media(max-width:768px){
          .sa-page{padding:16px 16px 60px;gap:16px;}
          .sa-grid-2,.sa-grid-3{grid-template-columns:1fr;}
          .sa-sp-grid{grid-template-columns:1fr 1fr;}
          .sa-modal{max-height:95vh;}
          .sa-card-body{padding:18px;}
          .sa-save-bar{flex-direction:column;align-items:stretch;}
          .sa-save-btn,.sa-cancel-btn{justify-content:center;}
        }
        @media(max-width:480px){
          .sa-sp-grid{grid-template-columns:1fr;}
        }
      `}</style>
    </App>
  );
}
